import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Dropdown/popover ancorato a un elemento trigger ma renderizzato in un PORTAL
 * su document.body con position:fixed. Cosi' NON viene tagliato dai contenitori
 * con overflow (es. le tabelle righe `overflow-x-auto` di PSC/LNP/MPG/CON/MSS,
 * che altrimenti clippano un dropdown `position:absolute`).
 *
 * - Si posiziona sotto al trigger (anchorRef); se sotto non c'e' spazio e sopra
 *   ce n'e' di piu', FLIPPA aprendosi verso l'alto (caso: trigger in fondo pagina,
 *   es. sezione "Entrate" del PSC, dove un dropdown verso il basso finirebbe
 *   sotto il viewport — e siccome e' position:fixed, la pagina non scrolla a
 *   rivelarlo).
 * - Limita l'altezza allo spazio disponibile nel viewport (maxHeight) con
 *   overflowY:auto come rete di sicurezza: se nemmeno il lato scelto basta
 *   (viewport molto basso), il contenuto scrolla invece di essere tagliato.
 * - Si riposiziona su scroll/resize e quando il contenuto cambia altezza
 *   (ResizeObserver — la lista risultati cresce/si restringe durante la ricerca).
 * - Si chiude al click esterno (onClose).
 * - Il contenuto (children) definisce larghezza/aspetto (es. `w-80 rounded-md ...`).
 */
export function PopoverPortal({
  anchorRef,
  open,
  onClose,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const popRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 4; // distacco verticale dal trigger
      const margin = 8; // respiro minimo dai bordi del viewport
      // Clamp orizzontale per non uscire dal viewport (dropdown larghi ~320-384px).
      const left = Math.round(Math.max(margin, Math.min(r.left, window.innerWidth - 400)));
      const spaceBelow = window.innerHeight - r.bottom - gap - margin;
      const spaceAbove = r.top - gap - margin;
      // Altezza naturale del contenuto: visibility:hidden mantiene il layout,
      // quindi offsetHeight e' valido gia' al primo passaggio (vedi fallback sotto).
      const popH = popRef.current?.offsetHeight ?? 0;
      // Flip verso l'alto solo se sotto il contenuto non ci sta E sopra c'e' piu' spazio.
      const openAbove = popH > spaceBelow && spaceAbove > spaceBelow;
      const base: CSSProperties = { position: 'fixed', left, zIndex: 1000, overflowY: 'auto' };
      if (openAbove) {
        setStyle({
          ...base,
          bottom: Math.round(window.innerHeight - r.top + gap),
          maxHeight: Math.max(0, Math.floor(spaceAbove)),
        });
      } else {
        setStyle({
          ...base,
          top: Math.round(r.bottom + gap),
          maxHeight: Math.max(0, Math.floor(spaceBelow)),
        });
      }
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro && popRef.current) ro.observe(popRef.current);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || popRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, anchorRef, onClose]);

  if (!open) return null;
  // Renderizziamo sempre quando open: al primo paint il popover e' invisibile
  // (visibility:hidden, layout preservato per misurarne l'altezza); il layout
  // effect calcola subito la posizione reale prima del paint -> nessun flash.
  return createPortal(
    <div
      ref={popRef}
      style={style ?? { position: 'fixed', top: 0, left: 0, visibility: 'hidden', zIndex: 1000 }}
    >
      {children}
    </div>,
    document.body,
  );
}
